import React from 'react';
import { Trash2 } from 'lucide-react';
import type { HandbookChangeRequest, HandbookPolicyVersion } from '../../types/handbookPolicy';
import { HANDBOOK_CR_STATUS_LABELS } from '../../types/handbookPolicy';

interface HandbookPolicyHistoryRailProps {
  versions: HandbookPolicyVersion[];
  changeRequests: HandbookChangeRequest[];
  currentVersion: string;
  selectedVersionId: string | null;
  selectedChangeRequestId: string | null;
  canDeleteChangeRequest?: (cr: HandbookChangeRequest) => boolean;
  onSelectVersion: (versionId: string | null) => void;
  onSelectChangeRequest: (crId: string) => void;
  onDeleteChangeRequest?: (cr: HandbookChangeRequest) => void;
}

function formatWhen(d: Date): string {
  if (!d || d.getTime() === 0) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const HandbookPolicyHistoryRail: React.FC<HandbookPolicyHistoryRailProps> = ({
  versions,
  changeRequests,
  currentVersion,
  selectedVersionId,
  selectedChangeRequestId,
  canDeleteChangeRequest,
  onSelectVersion,
  onSelectChangeRequest,
  onDeleteChangeRequest,
}) => {
  return (
    <aside className="people-handbook-catalog-rail" aria-label="Version history">
      <section>
        <h3 className="people-handbook-rail-heading">Versions</h3>
        {versions.length === 0 ? (
          <p className="people-hub-muted people-handbook-rail-empty">History will appear after the first snapshot.</p>
        ) : (
          <ul className="people-handbook-rail-list">
            {versions.map(v => {
              const isCurrent = !selectedVersionId && v.version === currentVersion;
              const selected = selectedVersionId === v.id || isCurrent;
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    className={`people-handbook-rail-item ${selected ? 'active' : ''}`}
                    onClick={() => onSelectVersion(v.version === currentVersion ? null : v.id)}
                  >
                    <span className="people-handbook-rail-item-title">
                      v{v.version}
                      {v.version === currentVersion ? (
                        <span className="ops-stat ops-stat-done">Current</span>
                      ) : null}
                    </span>
                    <span className="people-handbook-rail-item-meta">
                      {formatWhen(v.publishedAt)}
                      {v.publishedByName ? ` · ${v.publishedByName}` : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h3 className="people-handbook-rail-heading">Change requests</h3>
        {changeRequests.length === 0 ? (
          <p className="people-hub-muted people-handbook-rail-empty">No change requests yet.</p>
        ) : (
          <ul className="people-handbook-rail-list">
            {changeRequests.map(cr => {
              const selected = selectedChangeRequestId === cr.id;
              const canDelete = Boolean(canDeleteChangeRequest?.(cr));
              return (
                <li key={cr.id} className="people-handbook-rail-cr">
                  <button
                    type="button"
                    className={`people-handbook-rail-item ${selected ? 'active' : ''}`}
                    onClick={() => onSelectChangeRequest(cr.id)}
                  >
                    <span className="people-handbook-rail-item-title">
                      {cr.title || 'Untitled change'}
                      <span className={`ops-stat people-handbook-cr-pill people-handbook-cr-pill-${cr.status}`}>
                        {HANDBOOK_CR_STATUS_LABELS[cr.status]}
                      </span>
                    </span>
                    <span className="people-handbook-rail-item-meta">
                      {cr.authorName}
                      {cr.baseVersion ? ` · base v${cr.baseVersion}` : ''}
                    </span>
                  </button>
                  {canDelete ? (
                    <button
                      type="button"
                      className="people-handbook-rail-delete"
                      aria-label={`Delete change request ${cr.title || cr.id}`}
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteChangeRequest?.(cr);
                      }}
                    >
                      <Trash2 size={13} aria-hidden />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </aside>
  );
};

export default HandbookPolicyHistoryRail;
